import {
  Fragment,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import type {
  NetworkConnectionListItem,
  NetworkConnectionUser,
  NetworkDomainHost,
  NetworkDomainHostDecision,
  NetworkDomainHostEffectiveDecision,
  SignatureStatus,
} from '@kirin/api-client/contracts/shadow-ai';
import { confidenceLabelLabels } from '@kirin/api-client/contracts/shadow-ai';
import {
  useNetworkConnectionUsers,
  useNetworkDomainHosts,
  useSetNetworkDomainHostDecision,
} from '@/hooks/api/useShadowAiTools';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { EndpointDetailsPopover } from '@/components/Inventory/EndpointDetailsPopover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { cn } from '@/lib/utils';
import { TimestampFormatContext } from '@/contexts/TimestampFormatContext';
import { getTimestampColumnWidth } from '@/components/shared/timestampColumnWidth';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import {
  NETWORK_DOMAIN_COLUMN_WIDTHS,
  NETWORK_DOMAIN_EXPANDER_WIDTH,
  NETWORK_DOMAIN_SELECT_WIDTH,
} from './columnWidths';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TruncatedText } from '@/components/shared/TruncatedText';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { HostClassificationBadge } from './CellRenderers';
import {
  hostDecisionSource,
  isGroupedRow,
  isOverriddenHost,
  orderHostsForScope,
} from './grouping';
import type { NetworkStatusFilterValue } from './NetworkStatusFilterSelector';
import { formatBytes } from '../shared';
import { formatDateTime, formatZonedDate } from '@/lib/date-utils';
import { EndpointSearchInput } from '@/components/shared/EndpointSearchInput';
import { useEndpointSearch } from '@/components/shared/endpointSearch';
import { usersEndpointsLabel } from '@/components/shared/shadow-ai/usersEndpointsLabel';

function IndicatorBadge({
  active,
  label,
}: {
  active: boolean | null | undefined;
  label: string;
}): ReactElement {
  return (
    <span
      className={cn(
        'px-2 py-0.5 rounded text-xs',
        active
          ? 'bg-blue-500/20 text-blue-400'
          : 'bg-muted text-muted-foreground/60'
      )}
    >
      {active ? '✓' : '−'} {label}
    </span>
  );
}

/**
 * One label/value line, omitted entirely when the value is absent — most
 * fields are legitimately unavailable on most platforms, so the panels stay
 * sparse instead of filling with "—" placeholders (KIR-4588).
 */
function DetailRow({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}): ReactElement | null {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex gap-2">
      <span className="w-28 shrink-0 text-muted-foreground">{label}</span>
      {/* Clipped to one line with the full value on hover, like every other
          long value in this table. A binary path is the case that forced it:
          `/Applications/Google Chrome.app/.../Google Chrome Helper` is wider
          than its half of the grid, and relying on the value wrapping left it
          one stylesheet change away from painting over Connection detail --
          which is exactly what it did. Truncation cannot overflow at all.
          `min-w-0` on the flex item is what lets it shrink below its content. */}
      <span className="min-w-0 flex-1">
        <TruncatedText className="font-medium">{String(value)}</TruncatedText>
      </span>
    </div>
  );
}

/**
 * The code-signature verdict (KIR-4588).
 *
 * `unsigned` is the security-interesting case, so it is stated in amber
 * rather than left blank. A null status means we could not check — not that
 * the binary is untrusted — so that renders nothing at all: an absent line
 * must never read as a warning.
 */
function SignatureRow({
  status,
  signer,
}: {
  status: SignatureStatus | null;
  signer: string | null;
}): ReactElement | null {
  if (status === null) return null;

  if (status === 'unsigned') {
    return (
      <div className="flex gap-2">
        <span className="w-28 shrink-0 text-muted-foreground">Signature</span>
        <span className="min-w-0 font-medium text-amber-600 dark:text-amber-400">
          Unsigned or invalid
        </span>
      </div>
    );
  }

  if (status === 'adhoc') {
    return <DetailRow label="Signature" value="Ad-hoc (no identity)" />;
  }

  return (
    <DetailRow
      label="Signed by"
      value={
        signer || (status === 'platform' ? 'Operating system' : 'Verified')
      }
    />
  );
}

/**
 * Human label for the capture transport stored in `source`.
 *
 * Only `tls` names a protocol the connection actually spoke. The others are
 * how the observation reached us: a DNS query (macOS pcap or the Windows
 * DNS-Client ETW provider), or the streaming heuristic. Presenting those
 * under a "protocol" label would state something the capture never
 * established, so they are labelled for what they are (KIR-4588 review).
 */
function captureProtocol(source: string | null): string | null {
  switch (source) {
    case 'tls':
      return 'TLS';
    case 'dns':
      return 'DNS query';
    case 'etw_dns':
      return 'DNS query (ETW)';
    case 'streaming':
      return 'Streaming';
    default:
      return source;
  }
}

/** Per-host triage labels. `effectiveDecision` is the resolved precedence. */
const HOST_DECISION_LABELS: Record<NetworkDomainHostEffectiveDecision, string> =
  {
    identified: 'Identified',
    dismissed: 'Dismissed',
    unreviewed: 'Unreviewed',
  };

function hostDecisionLabel(
  decision: NetworkDomainHostEffectiveDecision
): string {
  return HOST_DECISION_LABELS[decision];
}

/**
 * A field that is per-EVENT but belongs at host level.
 *
 * The mockup's rule is "show a field once only if it is the same for every
 * user; if it can change per user, show it only in the Users table". Port,
 * protocol, binary path and signature sit awkwardly between the two: KIR-4948
 * calls them host level, but they come from events and nothing stops two users
 * reaching the same host on different ports.
 *
 * So a value is shown once only when every attribution agrees, and otherwise
 * says how many answers exist rather than choosing one. Choosing one is exactly
 * what this part of the ticket exists to stop.
 */
function distinctAcross<T>(
  users: NetworkConnectionUser[],
  pick: (user: NetworkConnectionUser) => T | null | undefined
): { value: T | null; count: number } {
  const seen = new Map<string, T>();
  for (const user of users) {
    const value = pick(user);
    if (value === null || value === undefined || value === '') continue;
    seen.set(String(value), value);
  }
  const values = [...seen.values()];
  return {
    value: values.length === 1 ? (values[0] as T) : null,
    count: values.length,
  };
}

/** {@link DetailRow} for a field aggregated across one host's users. */
function AggregatedDetailRow<T extends string | number>({
  label,
  users,
  pick,
  format,
}: {
  label: string;
  users: NetworkConnectionUser[];
  pick: (user: NetworkConnectionUser) => T | null | undefined;
  format?: (value: T) => string | number | null;
}): ReactElement | null {
  const { value, count } = distinctAcross(users, pick);
  if (count === 0) return null;
  if (count > 1) {
    return <DetailRow label={label} value={`${count} values across users`} />;
  }
  const single = value as T;
  return <DetailRow label={label} value={format ? format(single) : single} />;
}

/**
 * What is true of the HOST itself, shown once (KIR-4948, mockup "One host, many
 * users").
 *
 * Replaces the old `ProcessConnectionDetail`, which took the highest-hit-count
 * attribution and rendered ITS pid, local user, machine and port as though they
 * described the host — its own JSDoc admitted as much. Those are per-user by
 * nature and now appear in the users table, one line each. What stays here is
 * either genuinely host level (the destination, the host's last seen) or
 * aggregated across every attribution, so no single user speaks for the host.
 */
function HostFactsPanel({
  host,
  users,
  lastSeen,
}: {
  host: string;
  users: NetworkConnectionUser[];
  lastSeen: string;
}): ReactElement | null {
  const timeZone = useDisplayTimeZone();
  // KIR-4588's rule, kept: with no attribution there is nothing to aggregate,
  // and a panel of empty labels reads as "we looked and found nothing" rather
  // than "nobody has reported traffic for this host yet".
  if (users.length === 0) return null;

  const signerStatuses = distinctAcross(users, u => u.signerStatus);
  const signers = distinctAcross(users, u => u.signer);

  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <div className="min-w-0 space-y-1 text-xs">
        <div className="mb-2 font-medium uppercase tracking-wide text-muted-foreground">
          Process detail
        </div>
        <AggregatedDetailRow
          label="Process name"
          users={users}
          pick={u => u.processName}
        />
        <AggregatedDetailRow
          label="Binary path"
          users={users}
          pick={u => u.processPath}
        />
        {signerStatuses.count > 1 ? (
          <DetailRow
            label="Signature"
            value={`${signerStatuses.count} values across users`}
          />
        ) : (
          <SignatureRow
            status={signerStatuses.value}
            signer={signers.count === 1 ? signers.value : null}
          />
        )}
      </div>
      <div className="min-w-0 space-y-1 text-xs">
        <div className="mb-2 font-medium uppercase tracking-wide text-muted-foreground">
          Connection detail
        </div>
        <DetailRow label="Destination host" value={host} />
        <AggregatedDetailRow
          label="Destination IP"
          users={users}
          pick={u => u.dstIp}
        />
        <AggregatedDetailRow label="Port" users={users} pick={u => u.port} />
        <AggregatedDetailRow
          label="Protocol"
          users={users}
          pick={u => u.source}
          format={v => captureProtocol(String(v))}
        />
        <DetailRow
          label="Last seen"
          value={formatDateTime(lastSeen, timeZone)}
        />
      </div>
    </div>
  );
}

const endpointOfConnectionUser = (
  user: NetworkConnectionUser
): { name: string | null | undefined; machineId: string } => ({
  name: user.endpointName,
  machineId: user.machineId,
});

/**
 * One line per user (KIR-4948: "We never pick one user to stand for the host").
 *
 * Local user, machine and PID moved here from the panel above, which used to
 * show them once using the busiest attribution's values. First seen is new —
 * the field was already on the contract and had simply never been rendered.
 *
 * A real table rather than the previous flex rows: ten columns do not line up
 * reliably as flex children, and the horizontal scroll is confined to this
 * element so the expanded row itself never scrolls sideways.
 */
function UsersTable({
  users,
  endpointEnabled,
  caption,
}: {
  users: NetworkConnectionUser[];
  endpointEnabled: boolean;
  caption: string;
}): ReactElement | null {
  // KIR-5098: search the rows by endpoint name or ID.
  const { query, setQuery, filtered, isFiltering } = useEndpointSearch(
    users,
    endpointOfConnectionUser
  );
  if (users.length === 0) return null;
  const cell = 'px-2 py-1 align-middle whitespace-nowrap';

  return (
    <div className="min-w-0 rounded-lg border border-border bg-muted p-3">
      <div className="mb-2 text-xs text-muted-foreground">{caption}</div>
      <EndpointSearchInput value={query} onChange={setQuery} className="mb-2" />
      {isFiltering && filtered.length === 0 ? (
        <div className="text-xs text-muted-foreground">
          No users on a matching endpoint
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className={cn(cell, 'text-left font-normal')}>User</th>
                <th className={cn(cell, 'text-left font-normal')}>
                  {endpointEnabled ? 'Endpoint' : 'Machine'}
                </th>
                <th className={cn(cell, 'text-left font-normal')}>
                  Local user
                </th>
                <th className={cn(cell, 'text-left font-normal')}>PID</th>
                <th className={cn(cell, 'text-left font-normal')}>Process</th>
                <th className={cn(cell, 'text-left font-normal')}>Hits</th>
                <th className={cn(cell, 'text-left font-normal')}>Traffic</th>
                <th className={cn(cell, 'text-left font-normal')}>JA4</th>
                <th className={cn(cell, 'text-left font-normal')}>
                  First seen
                </th>
                <th className={cn(cell, 'text-left font-normal')}>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {/*
              Keyed by position, not identity. One event row is unique on
              (organization, user, machine, raw domain) -- `sni_event_unique` --
              and the raw domain is the only one of those four the contract does
              not carry, so two spellings of the same host (a trailing dot, a
              port) by one user on one machine arrive as two rows that no
              combination of the fields here can tell apart. Keying on those
              fields collapses the pair and silently drops an attribution.
              Position is safe for this table: it is read-only, holds no local
              state, and is replaced wholesale on every fetch.
            */}
              {filtered.map((user, index) => (
                <tr key={index}>
                  <td className={cn(cell, 'max-w-[12rem] truncate')}>
                    {user.name || user.email || 'Unknown'}
                  </td>
                  <td className={cn(cell, 'max-w-[10rem] overflow-hidden')}>
                    {endpointEnabled ? (
                      <EndpointDetailsPopover
                        userId={user.userId}
                        machineId={user.machineId}
                        endpointName={user.endpointName}
                        triggerClassName="max-w-full"
                      />
                    ) : (
                      <CopyableCode value={user.machineId} truncateAt={10} />
                    )}
                  </td>
                  <td className={cn(cell, 'max-w-[10rem] truncate')}>
                    {user.localUser ?? (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className={cell}>
                    {user.pid ?? (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className={cn(cell, 'max-w-[10rem] truncate')}>
                    {user.processName ?? (
                      <span className="italic text-muted-foreground">
                        Unknown
                      </span>
                    )}
                  </td>
                  <td className={cell}>{user.hitCount}</td>
                  <td className={cn(cell, 'tabular-nums')}>
                    {formatBytes((user.bytesIn ?? 0) + (user.bytesOut ?? 0))}
                  </td>
                  <td className={cell}>
                    {user.ja4 ? (
                      <CopyableCode value={user.ja4} truncateAt={10} />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className={cn(cell, 'text-muted-foreground')}>
                    <RelativeTimeWithTooltip date={user.firstSeen} inline />
                  </td>
                  <td className={cn(cell, 'text-muted-foreground')}>
                    <RelativeTimeWithTooltip date={user.lastSeen} inline />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * The value the Status menu uses for "no override". The mutation sends `null`,
 * which a Radix Select cannot carry as an item value.
 */
const HOST_STATUS_INHERIT = '__inherit__';

/**
 * Per-host Status menu (KIR-4948 mockup "Domain review: status lives per host").
 *
 * Replaces three ghost buttons. The trigger always shows the EFFECTIVE status,
 * so a host reads the same whether it inherits or dissents, while the selected
 * value is the host's own override — which is what "Inherit domain status"
 * returns it to, and the only way back, since a domain action never clears an
 * override.
 */
function HostStatusSelect({
  host,
  disabled,
  onChange,
}: {
  host: NetworkDomainHost;
  disabled: boolean;
  onChange: (decision: NetworkDomainHostDecision | null) => void;
}): ReactElement {
  return (
    <Select
      // A host the last domain action mirrored onto carries a `decision` it
      // never chose, and preselecting that made the menu claim the reviewer had
      // already set this host. Only a manual override is the host's own answer;
      // everything else is still inheriting.
      value={
        isOverriddenHost(host) && host.decision !== null
          ? host.decision
          : HOST_STATUS_INHERIT
      }
      disabled={disabled}
      onValueChange={next =>
        onChange(
          next === HOST_STATUS_INHERIT
            ? null
            : (next as NetworkDomainHostDecision)
        )
      }
    >
      <SelectTrigger
        className="h-7 w-[150px]"
        aria-label={`Status for ${host.host}`}
      >
        {/* Children override the selected item's label on purpose: the menu is
            keyed on the override, the trigger states the resolved answer. */}
        <SelectValue>{hostDecisionLabel(host.effectiveDecision)}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="identified">Identified</SelectItem>
        <SelectItem value="dismissed">Dismissed</SelectItem>
        <SelectItem value={HOST_STATUS_INHERIT}>
          Inherit domain status
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

/**
 * Who set the status this host shows, and when (KIR-4948 mockup).
 *
 * The attribution follows the same precedence as the status beside it: an
 * overridden host names whoever overrode it, an inheriting one names whoever
 * classified the domain. The closing line says which of the two this is, so an
 * inherited status is never mistaken for a decision made about this host.
 */
function HostClassificationPanel({
  host,
}: {
  host: NetworkDomainHost;
}): ReactElement {
  const timeZone = useDisplayTimeZone();
  const who = host.decidedByName || host.decidedByEmail;
  // KIR-5168: a host Kirin decided itself has no reviewer to name; "Not yet
  // reviewed" beside an Identified status would contradict it.
  const source = hostDecisionSource(host);

  return (
    <div className="border-l-2 border-blue-500 pl-3">
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Classification
      </div>
      <div className="space-y-1 text-xs">
        <DetailRow
          label="Set by"
          value={
            source === 'builtin'
              ? 'Kirin (builtin)'
              : (who ?? (host.decidedAt ? 'Unknown' : 'Not yet reviewed'))
          }
        />
        <DetailRow
          label="Set on"
          value={
            host.decidedAt ? formatDateTime(host.decidedAt, timeZone) : null
          }
        />
        <div className="pt-1 text-muted-foreground">
          {isOverriddenHost(host)
            ? 'Overridden on this host. Choose “Inherit domain status” to follow the domain again.'
            : source === 'builtin'
              ? 'Set by Kirin’s built-in list. Change this host from the Status menu on its row (override).'
              : 'Inherited from the domain. Change this host from the Status menu on its row (override).'}
        </div>
      </div>
    </div>
  );
}

/**
 * Everything behind one expanded HOST: who classified it, what is true of the
 * host itself, and every user that reached it.
 *
 * The users query is scoped to this host rather than to the whole service
 * (KIR-4948) — a grouped row can roll up dozens of unrelated hosts, and a
 * service-wide list under one of them would name users who never touched it.
 */
function ExpandedHostDetail({
  organizationId,
  domain,
  host,
  endpointEnabled,
}: {
  organizationId: string;
  domain: string;
  host: NetworkDomainHost;
  endpointEnabled: boolean;
}): ReactElement {
  // No process filter, unlike the ungrouped row above. That row narrows to its
  // own process as long-standing behaviour, not because it is a domain+process
  // pair -- the ungrouped list is `GROUP BY LOWER(domain)` with a `MODE()`
  // process too. Here the parent row's process is the dominant one across the
  // whole SERVICE, so applying it to one host is arbitrary: the host states its
  // own `via …` and can have been reached by a different process, whose users
  // would then silently vanish. The panel aggregates what it finds instead,
  // which is what it does for every other per-event field.
  const { data, isLoading } = useNetworkConnectionUsers(
    organizationId,
    domain,
    true,
    null,
    host.host
  );
  const users = data?.users ?? [];

  return (
    <div className="space-y-4">
      <HostClassificationPanel host={host} />
      <HostFactsPanel host={host.host} users={users} lastSeen={host.lastSeen} />
      {isLoading ? (
        <div className="text-xs text-muted-foreground">Loading users…</div>
      ) : (
        <UsersTable
          users={users}
          endpointEnabled={endpointEnabled}
          // KIR-5281: rows are per user endpoint, so say both counts.
          caption={`Users of ${host.host} (${usersEndpointsLabel(users)})`}
        />
      )}
    </div>
  );
}

/** Host rows kept on screen before the list scrolls. */
const MAX_VISIBLE_GROUPED_HOSTS = 10;

/**
 * Stand-in until the first rows are measured. Parent discovery rows are
 * `h-12` (48px); a host row is the status trigger (`h-7`) plus `py-2`, in
 * the same range. The measured height replaces this before paint.
 */
const GROUPED_HOST_ROW_FALLBACK_PX = 48;

/**
 * KIR-4838: the hosts rolled up under a grouped row, rendered as a sub-table
 * that lines up with the columns of the table above it.
 *
 * The alignment is structural rather than eyeballed: a `colgroup` replays the
 * exact widths the parent table uses (`NETWORK_DOMAIN_COLUMN_WIDTHS`, plus the
 * chevron gutter and the format-dependent Last Seen width), and the cells carry
 * the same `px-3 py-2` the DataTable gives its own cells. The parent runs
 * `tableLayout: 'fixed'`, so those widths are exact and the two grids match.
 * `-mx-4` cancels the expansion container's padding so column one starts at the
 * table's left edge instead of 16px into it.
 *
 * KIR-4948: each host row now expands in turn, which is where its per-user
 * detail lives. Users and Endpoints still read as a dash on the collapsed row —
 * those are distinct counts the hosts endpoint does not carry, and repeating
 * the domain-wide figure on every host would overstate each one — but the
 * expansion answers the question properly for one host at a time.
 *
 * Rendered for every SERVICE row, including one whose service has a single
 * host: that row is still a service, and its one host is where the per-host
 * Status menu, `New` flag and Classification panel live. Gated on
 * `row.grouped` by the caller, so the ungrouped page (flag off, every row a
 * host) is visually unchanged.
 */
function GroupedHostList({
  organizationId,
  domain,
  hostCount,
  endpointEnabled,
  showAgentColumn,
  showSelectionColumn,
  statusScope,
}: {
  organizationId: string;
  domain: string;
  hostCount: number;
  endpointEnabled: boolean;
  showAgentColumn: boolean;
  showSelectionColumn: boolean;
  statusScope: NetworkStatusFilterValue;
}): ReactElement | null {
  const { value: timestampFormat } = useContext(TimestampFormatContext);
  const timeZone = useDisplayTimeZone();
  const { data, isLoading } = useNetworkDomainHosts(organizationId, domain);
  const setDecision = useSetNetworkDomainHostDecision(organizationId, domain);
  const [expandedHost, setExpandedHost] = useState<string | null>(null);
  const hosts = useMemo(
    () => orderHostsForScope(data?.hosts ?? [], statusScope),
    [data?.hosts, statusScope]
  );
  const hostsOverflow = hosts.length > MAX_VISIBLE_GROUPED_HOSTS;
  const hostsScrollRef = useRef<HTMLDivElement>(null);

  // The cap is the height of the first ten host rows, not a guessed pixel
  // size: the status control and the New/confidence badges make a row taller
  // than plain text. Only rows marked `data-grouped-host-row` count. An
  // expanded host's detail (and the user rows inside it) also contain
  // `button[aria-expanded]` — the endpoint popover — and folding those in
  // on a refetch would grow the cap past ten hosts. Written onto the node
  // directly so it lands before paint without a second render.
  useLayoutEffect(() => {
    const root = hostsScrollRef.current;
    if (!root) return;
    if (!hostsOverflow) {
      root.style.maxHeight = '';
      return;
    }
    const hostRows = [
      ...root.querySelectorAll<HTMLTableRowElement>(
        'tr[data-grouped-host-row]'
      ),
    ];
    let height = 0;
    const visible = Math.min(MAX_VISIBLE_GROUPED_HOSTS, hostRows.length);
    for (let index = 0; index < visible; index++) {
      height += hostRows[index].getBoundingClientRect().height;
    }
    root.style.maxHeight =
      height > 0
        ? `${Math.ceil(height)}px`
        : `${MAX_VISIBLE_GROUPED_HOSTS * GROUPED_HOST_ROW_FALLBACK_PX}px`;
  }, [hostsOverflow, hosts]);

  const cell = 'px-3 py-2 align-middle';
  // Matches the colgroup below, so the detail row spans the whole sub-table
  // rather than stopping short and dragging the columns out of line.
  const columnCount =
    10 + (showSelectionColumn ? 1 : 0) + (showAgentColumn ? 1 : 0);

  return (
    <div className="-mx-4">
      {/* KIR-5281: the hosts endpoint now lists only hosts a current member
          reached -- the same events hostCount is computed from -- so the list
          is never longer than the badge. */}
      <div className="px-4 pb-1 text-xs text-muted-foreground">
        Hosts ({hostCount})
      </div>
      {isLoading && (
        <div className="px-4 text-xs text-muted-foreground">Loading hosts…</div>
      )}
      {/* The row comes from the EVENTS, the sub-table from the service tables,
          and the two are filled by different jobs. Between them a service can
          be listed with no host rows at all -- one seen only in history the
          aggregate pass has not reached, or one whose roll-up failed inside the
          savepoint processReport swallows. Without this the panel rendered
          "Hosts (3)" above an empty table and left the reviewer to guess
          whether that meant "none" or "not yet". */}
      {!isLoading && hosts.length === 0 && (
        <div className="px-4 pb-2 text-xs text-muted-foreground">
          No per-host breakdown yet — this service&rsquo;s hosts are still being
          rolled up. The totals above are complete; the users below cover the
          whole service meanwhile.
        </div>
      )}
      {/* Only flagged when the list is SHORTER than the badge: both now count
          member-reached hosts (KIR-5281), so shorter means not yet rolled up. */}
      {!isLoading && hosts.length > 0 && hosts.length < hostCount && (
        <div className="px-4 pb-2 text-xs text-muted-foreground">
          Showing {hosts.length} of {hostCount} hosts — the rest are still being
          rolled up.
        </div>
      )}
      <div
        ref={hostsScrollRef}
        data-testid="grouped-hosts-scroll"
        className={cn(hostsOverflow && 'overflow-y-auto overflow-x-hidden')}
      >
        <table className="w-full table-fixed text-xs">
          <colgroup>
            <col style={{ width: NETWORK_DOMAIN_EXPANDER_WIDTH }} />
            {showSelectionColumn && (
              <col style={{ width: NETWORK_DOMAIN_SELECT_WIDTH }} />
            )}
            {showAgentColumn && (
              <col style={{ width: NETWORK_DOMAIN_COLUMN_WIDTHS.agent }} />
            )}
            <col style={{ width: NETWORK_DOMAIN_COLUMN_WIDTHS.domain }} />
            <col style={{ width: NETWORK_DOMAIN_COLUMN_WIDTHS.processName }} />
            <col style={{ width: NETWORK_DOMAIN_COLUMN_WIDTHS.status }} />
            <col style={{ width: NETWORK_DOMAIN_COLUMN_WIDTHS.totalBytes }} />
            <col style={{ width: NETWORK_DOMAIN_COLUMN_WIDTHS.hitCount }} />
            <col style={{ width: NETWORK_DOMAIN_COLUMN_WIDTHS.uniqueUsers }} />
            <col
              style={{ width: NETWORK_DOMAIN_COLUMN_WIDTHS.uniqueMachines }}
            />
            <col
              style={{
                width: getTimestampColumnWidth(timestampFormat, timeZone),
              }}
            />
            <col style={{ width: NETWORK_DOMAIN_COLUMN_WIDTHS.actions }} />
          </colgroup>
          <tbody>
            {hosts.map(h => {
              const isExpanded = expandedHost === h.host;
              return (
                <Fragment key={h.host}>
                  <tr
                    className="border-t border-border/50"
                    data-grouped-host-row=""
                  >
                    {/* Indented past the parent row's chevron rather than
                      centred under it. Both tables reserve the same 40px
                      gutter, so a centred chevron here sat directly beneath
                      the domain's own and the two levels read as one flat list
                      instead of a nesting.
                      `pl-7` puts it past the end of that gutter, so it extends
                      a few pixels into the next cell. Deliberate, and safe:
                      this cell sets no `overflow-hidden`, and whichever column
                      follows (the selection gutter, or the Host cell with its
                      own `px-3`) has empty padding there to extend into. The
                      colgroup is untouched, so the Host column itself still
                      starts exactly where the parent's does -- the alignment
                      the shared widths exist for, pinned by the column-layout
                      test. */}
                    <td className={cn(cell, 'pl-7 pr-0 text-left')}>
                      <button
                        type="button"
                        aria-expanded={isExpanded}
                        aria-label={
                          isExpanded ? `Collapse ${h.host}` : `Expand ${h.host}`
                        }
                        className="text-muted-foreground hover:text-foreground"
                        onClick={() =>
                          setExpandedHost(isExpanded ? null : h.host)
                        }
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                    {showSelectionColumn && <td />}
                    {showAgentColumn && <td />}
                    {/* Clipped, not wrapped: the parent table gives its cells
                    `overflow-hidden text-ellipsis` and `TruncatedText` adds the
                    hover tooltip, so a long host reads the same in both grids
                    instead of wrapping and pushing the sub-table's rows out of
                    line with the ones above. */}
                    <td className={cn(cell, 'overflow-hidden')}>
                      <div className="flex items-center gap-1.5">
                        <TruncatedText className="font-mono">
                          {h.host}
                        </TruncatedText>
                        {/* KIR-4588 item 2: the host inherits the domain's
                        decision, and still says nobody has looked at it. */}
                        {h.isNew && (
                          <span
                            className="shrink-0 rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-600 dark:text-amber-400"
                            title="First seen after this domain was classified. It follows the domain's status, but nobody has reviewed this host yet."
                          >
                            New
                          </span>
                        )}
                        {/* KIR-5168: the one place a host states what it is
                        -- the confidence guess while unreviewed, then AI / Not
                        AI and who decided it. An override used to be told
                        apart by a chip beside the Status menu instead, where
                        the column is too narrow to show it. */}
                        <HostClassificationBadge host={h} />
                      </div>
                    </td>
                    <td
                      className={cn(
                        cell,
                        'overflow-hidden text-muted-foreground'
                      )}
                    >
                      {h.lastProcessName ? (
                        <TruncatedText>{`via ${h.lastProcessName}`}</TruncatedText>
                      ) : null}
                    </td>
                    <td className={cn(cell, 'overflow-hidden')}>
                      <HostStatusSelect
                        host={h}
                        disabled={setDecision.isPending}
                        onChange={decision =>
                          setDecision.mutate({ host: h.host, decision })
                        }
                      />
                    </td>
                    <td className={cn(cell, 'tabular-nums')}>
                      {formatBytes(h.bytesIn + h.bytesOut)}
                    </td>
                    <td className={cell}>{h.hitCount}</td>
                    {/* KIR-4750: the same users the host's expanded table
                        lists. Null only from an API that predates the field. */}
                    {typeof h.uniqueUsers === 'number' ? (
                      <td className={cell}>{h.uniqueUsers}</td>
                    ) : (
                      <td className={cn(cell, 'text-muted-foreground')}>
                        <span title="Distinct users are counted for the domain, not per host. Expand this host to see its users.">
                          -
                        </span>
                      </td>
                    )}
                    <td className={cn(cell, 'text-muted-foreground')}>
                      <span title="Distinct endpoints are counted for the domain, not per host. Expand this host to see its endpoints.">
                        -
                      </span>
                    </td>
                    <td className={cn(cell, 'text-muted-foreground')}>
                      <RelativeTimeWithTooltip date={h.lastSeen} />
                    </td>
                    <td className={cell} />
                  </tr>
                  {isExpanded && (
                    <tr className="border-t border-border/50 bg-muted/30">
                      <td colSpan={columnCount} className="px-4 py-3">
                        <ExpandedHostDetail
                          organizationId={organizationId}
                          domain={domain}
                          host={h}
                          endpointEnabled={endpointEnabled}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CopyableCode({
  value,
  truncateAt = 16,
}: {
  value: string;
  truncateAt?: number;
}): ReactElement {
  const [copied, setCopied] = useState(false);
  const isTruncated = value.length > truncateAt;
  const displayValue = isTruncated
    ? `${value.substring(0, truncateAt)}...`
    : value;

  const handleCopy = async (): Promise<void> => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!isTruncated) {
    return <code className="text-xs bg-muted px-2 py-1 rounded">{value}</code>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={handleCopy}
          className="text-xs bg-muted px-2 py-1 rounded font-mono cursor-pointer hover:bg-muted/80 transition-colors"
        >
          {copied ? '✓ Copied' : displayValue}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs break-all font-mono">
        {value}
        <div className="text-[10px] text-muted-foreground mt-1">
          Click to copy
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * How many JA4 fingerprints the TLS Fingerprints row shows inline before it
 * collapses the rest behind the "+N more" counter.
 */
const JA4_VISIBLE_LIMIT = 3;

/**
 * The "+N more" counter closing the TLS Fingerprints row, carrying the hidden
 * fingerprints in a tooltip and copying all of them on click.
 *
 * Padded like a `CopyableCode` chip (`px-2 py-1`): the row is a flex container,
 * so an unpadded span sat above the chips' centre line.
 *
 * The copy lives on the TRIGGER, not on a `CopyableCode` per line. Each visible
 * chip is already its own tooltip, so a copyable line here would be a tooltip
 * inside a tooltip, and Radix tooltips are not meant to hold controls at all --
 * they are unreachable by keyboard and touch, and the content dismisses on
 * pointer-down. One button copies the whole hidden set instead, which is what a
 * reviewer wanted the individual chips for.
 */
function Ja4OverflowBadge({
  hidden,
}: {
  hidden: readonly string[];
}): ReactElement {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (): Promise<void> => {
    await navigator.clipboard.writeText(hidden.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleCopy}
          className="text-xs text-muted-foreground px-2 py-1 rounded cursor-pointer hover:bg-muted/60 hover:text-foreground transition-colors"
        >
          {copied ? '✓ Copied' : `+${hidden.length} more`}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
          {hidden.length} more fingerprint{hidden.length === 1 ? '' : 's'}
        </div>
        <ul className="space-y-0.5 font-mono break-all">
          {hidden.map(ja4 => (
            <li key={ja4}>{ja4}</li>
          ))}
        </ul>
        <div className="text-[10px] text-muted-foreground mt-1">
          Click to copy all
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * KIR-3496: expanded-row detail for a Network Discovery domain — traffic
 * breakdown, TLS fingerprints, identification metadata, and the per-user table.
 * Ported from the old `ConnectionRow` expanded content.
 */
export function ExpandedConnectionRow({
  organizationId,
  row,
  // Mirror the parent table's two CONDITIONAL columns. Reserving a slot the
  // parent did not render shifts every host cell left of its column, so each
  // has to be tracked rather than assumed: the Agent column exists only on the
  // Identified scope, and the selection checkbox only on Unreviewed, because
  // bulk Identify/Dismiss act on pending rows alone.
  showAgentColumn = false,
  showSelectionColumn = false,
  // KIR-5168: the tab the table is on, so the hosts it is about list first.
  statusScope = 'all',
}: {
  organizationId: string;
  row: NetworkConnectionListItem;
  showAgentColumn?: boolean;
  showSelectionColumn?: boolean;
  statusScope?: NetworkStatusFilterValue;
}): ReactElement {
  const timeZone = useDisplayTimeZone();
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ENDPOINT_IDENTIFIER,
  ]);
  const endpointEnabled =
    flags[flagsmithFeatureFlags.ENDPOINT_IDENTIFIER] ?? false;
  // A grouped row's users belong to its individual HOSTS, and each host fetches
  // its own when expanded (KIR-4948). Asking for the service-wide list here
  // would duplicate those requests and reintroduce exactly the mixing this
  // ticket removes, so the query is off whenever the row groups.
  //
  // Read from the server's own answer rather than inferred from `hostCount > 1`.
  // A service with one visible host is still a SERVICE: `row.domain` is its
  // catalog key, so the inferred version handed `HostFactsPanel` a service key
  // to label "Destination host" -- `openai.com` for a row whose only host is
  // `api.openai.com` -- and hid the per-host Status menu, `New` flag and
  // Classification panel for it. `hostCount` is also computed per viewer, so a
  // member seeing one host of a multi-host service landed in the same place.
  //
  // `isGroupedRow` keeps the old guess as the fallback for an API that
  // predates the field, and is shared with the status pill so the two cannot
  // disagree about whether this row opens.
  const isGrouped = isGroupedRow(row);
  // Same query key as the sub-table's own call, so TanStack serves both from
  // one request -- this is an observation of that query, not a second one.
  const { data: groupedHosts, isLoading: groupedHostsLoading } =
    useNetworkDomainHosts(organizationId, row.domain, isGrouped);
  // Grouped, each host carries its own users table, so the service-wide query
  // is normally off. The exception is a service whose host rows have not been
  // rolled up yet: without this the expansion showed an empty hosts table AND
  // no users, which is strictly less than the ungrouped view gave for the same
  // service. Falling back keeps the panel answering the question it opened for.
  const groupedHostsEmpty =
    isGrouped &&
    !groupedHostsLoading &&
    (groupedHosts?.hosts.length ?? 0) === 0;
  // Named for the only grain it applies to: `processName` is a `MODE()` -- the
  // busiest process across everything the row rolls up -- and the users
  // endpoint applies it as an equality filter, so on a SERVICE it would drop
  // every user who reached it through anything else, under copy promising the
  // whole service. Same reason `ExpandedHostDetail` passes null.
  const hostProcessFilter = isGrouped ? null : row.processName;
  const { data: usersData } = useNetworkConnectionUsers(
    organizationId,
    row.domain,
    !isGrouped || groupedHostsEmpty,
    hostProcessFilter
  );

  const confidence = row.confidence;
  const ja4Count = row.ja4Fingerprints?.length ?? 0;
  const users = usersData?.users ?? [];

  return (
    <div className="space-y-4">
      {/* Ungrouped the row IS a host, so this is the "One host, many users"
          mockup exactly: the host's facts once, every user on its own line.
          Grouped, the hosts are a table of their own and sit at the bottom. */}
      {!isGrouped && (
        <HostFactsPanel
          host={row.domain}
          users={users}
          lastSeen={row.lastSeen}
        />
      )}
      <div className="flex gap-8">
        <div className="space-y-3 shrink-0">
          <div className="flex items-center gap-2">
            <IndicatorBadge active={row.isStreaming} label="Streaming" />
            <IndicatorBadge active={row.isProgrammatic} label="Programmatic" />
            <IndicatorBadge active={row.isAsymmetric} label="Asymmetric" />
            <IndicatorBadge active={row.isLongLived} label="Long-lived" />
            {row.status === 'pending' && confidence && (
              <span className="text-xs text-muted-foreground ml-2">
                {confidence.score}% {confidenceLabelLabels[confidence.label]}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
            <div>
              <span className="text-muted-foreground">Traffic In:</span>{' '}
              {formatBytes(row.totalBytesIn)}
            </div>
            <div>
              <span className="text-muted-foreground">Traffic Out:</span>{' '}
              {formatBytes(row.totalBytesOut)}
            </div>
            <div>
              <span className="text-muted-foreground">Ratio:</span>{' '}
              {row.totalBytesOut > 0
                ? `${Math.round(row.totalBytesIn / row.totalBytesOut)}:1`
                : 'N/A'}
            </div>
            <div>
              <span className="text-muted-foreground">First seen:</span>{' '}
              {formatZonedDate(row.firstSeen, timeZone)}
            </div>
          </div>
          {ja4Count > 0 && (
            <div className="text-xs">
              <span className="text-muted-foreground">
                TLS Fingerprints ({ja4Count}):
              </span>
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                {row.ja4Fingerprints.slice(0, JA4_VISIBLE_LIMIT).map(ja4 => (
                  <CopyableCode key={ja4} value={ja4} truncateAt={20} />
                ))}
                {ja4Count > JA4_VISIBLE_LIMIT && (
                  <Ja4OverflowBadge
                    hidden={row.ja4Fingerprints.slice(JA4_VISIBLE_LIMIT)}
                  />
                )}
              </div>
            </div>
          )}
          {(row.status === 'approved' || row.status === 'flagged') && (
            <div className="text-xs">
              {row.identifiedAgentName && (
                <div className="mb-1">
                  <span className="text-muted-foreground">Agent:</span>{' '}
                  <span className="font-medium">{row.identifiedAgentName}</span>
                </div>
              )}
              {row.classifiedBy && (
                <div className="text-muted-foreground">
                  Identified by{' '}
                  {row.classifiedByName || row.classifiedByEmail || 'Unknown'}
                  {row.classifiedAt &&
                    ` on ${formatZonedDate(row.classifiedAt, timeZone)}`}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Grouped, users belong to the individual host rows below. The
            exception is a service whose host rows have not been rolled up yet:
            there is nothing below to carry them, so the service-wide table
            stands in rather than showing the reviewer an empty panel. */}
        {(!isGrouped || groupedHostsEmpty) && (
          <div className="min-w-0 flex-1">
            <UsersTable
              users={users}
              endpointEnabled={endpointEnabled}
              // Follows the filter the query actually applied, not the row.
              caption={`Users (${usersEndpointsLabel(users)})${
                hostProcessFilter ? ` \u00b7 ${hostProcessFilter}` : ''
              }`}
            />
          </div>
        )}
      </div>

      {/* Hosts last. Everything above describes the service as a whole -- its
          traffic split, fingerprints and who classified it -- so it reads as a
          header for the rows beneath it. Leading with the host table pushed
          that summary below a list that is 24 rows long on a real service, so
          the thing the row is actually about scrolled out of view. */}
      {isGrouped && (
        <GroupedHostList
          organizationId={organizationId}
          domain={row.domain}
          hostCount={row.hostCount}
          endpointEnabled={endpointEnabled}
          showAgentColumn={showAgentColumn}
          showSelectionColumn={showSelectionColumn}
          statusScope={statusScope}
        />
      )}
    </div>
  );
}
